/**
 * Pure analytics helpers for the nuevo-flujo-de-agenda experiment.
 *
 * No DOM, no Alpine, no PostHog imports. All collaborators (analytics,
 * dispatch, normalize, etc.) are passed in by the caller so this module
 * can be loaded and asserted in plain Node.
 *
 * Variantes vigentes: "control" | "horario-visible".
 * Legacy "solo-agenda" es normalizada a "horario-visible" antes de cualquier
 * evento. Cualquier otro valor es descartado.
 */

const EXPERIMENT_KEY = 'ab-test-nuevo-flujo-de-agenda';
const ALLOWED_VARIANTS = Object.freeze(['control', 'horario-visible']);

/**
 * Normaliza la variante cruda del flag antes de cualquier uso analítico.
 * - null / undefined / ""         → null
 * - "control"                     → "control"
 * - "horario-visible"             → "horario-visible"
 * - "solo-agenda" (legacy)        → "horario-visible"
 * - cualquier otro string         → null (no se trackea)
 */
export function normalizeExperimentVariant(rawVariant) {
    if (rawVariant === null || rawVariant === undefined) {
        return null;
    }
    if (typeof rawVariant !== 'string') {
        return null;
    }
    const v = rawVariant.trim();
    if (v === '') {
        return null;
    }
    if (v === 'horario-visible') {
        return 'horario-visible';
    }
    if (v === 'solo-agenda') {
        return 'horario-visible';
    }
    if (v === 'control') {
        return 'control';
    }
    return null;
}

/**
 * Resuelve la variante que usa la UI a partir del valor crudo del flag.
 * PostHog devuelve `false` cuando el usuario queda fuera del experimento
 * (rollout, release conditions o flag apagado); ese valor y cualquier otro
 * fuera de la lista vigente caen a "control" para que los CTA se rendericen.
 *
 * @param {unknown} rawVariant
 * @returns {'control' | 'horario-visible'}
 */
export function resolveCalendarExperimentVariant(rawVariant) {
    return normalizeExperimentVariant(rawVariant) ?? 'control';
}

/**
 * Tracker idempotente para `visit_scheduling:experiment_exposure`.
 *
 * - Garantiza a lo sumo un track por instancia.
 * - Trackea solo si la variante resuelta está vigente y la unidad es elegible.
 * - Normaliza la variante antes de enviar el payload.
 *
 * Cada llamada retorna true si fue trackeada, false si fue omitida, o
 * 'already' si ya había sido trackeada antes (idempotencia).
 */
export function createExperimentExposureTracker({
    analytics,
    surface,
    experiment = EXPERIMENT_KEY,
    isUnitEligible,
}) {
    if (!analytics || typeof analytics.track !== 'function') {
        throw new Error('createExperimentExposureTracker: analytics.track requerido');
    }
    if (!surface || typeof surface !== 'string') {
        throw new Error('createExperimentExposureTracker: surface requerido');
    }
    if (typeof isUnitEligible !== 'function') {
        throw new Error('createExperimentExposureTracker: isUnitEligible requerido');
    }

    let fired = false;

    return {
        /**
         * Intenta registrar un exposure. Idempotente.
         *
         * @returns {'fired'|'omitted'|'already'}
         */
        track({ variant, unit }) {
            if (fired) {
                return 'already';
            }

            const normalized = normalizeExperimentVariant(variant);
            if (normalized === null) {
                return 'omitted';
            }

            if (!isUnitEligible(unit)) {
                return 'omitted';
            }

            analytics.track('visit_scheduling:experiment_exposure', {
                experiment,
                variant: normalized,
                surface,
            });

            fired = true;
            return 'fired';
        },
        /**
         * Solo para tests: permite resetear el flag y consultar si ya fue disparado.
         */
        __reset() {
            fired = false;
        },
        __hasFired() {
            return fired;
        },
    };
}

/**
 * Helper que decide si emitir visit_scheduling:date_pick.
 *
 * Emite SOLO si fromClick === true. Cualquier preselect programático
 * (false o undefined) NO emite.
 *
 * @returns {boolean} true si dispatchó, false si fue omitida por guard.
 */
export function shouldEmitDatePick({ fromClick }) {
    return fromClick === true;
}

/**
 * Helper que decide si emitir visit_scheduling:time_pick.
 *
 * El helper existe solo para dejar explícita la regla: actualmente,
 * time_pick SIEMPRE se emite cuando es invocado por una interacción
 * explícita del usuario. La no auto-selección está garantizada porque
 * ningún path programático llama a este dispatch.
 *
 * @returns {boolean} true (siempre, mientras el caller venga de click).
 */
export function shouldEmitTimePick() {
    return true;
}

/**
 * Construye el payload de date_pick. variant debe venir ya normalizada.
 */
export function buildDatePickPayload({ date, variant, surface }) {
    return { date, variant, surface };
}

/**
 * Construye el payload de time_pick. variant debe venir ya normalizada.
 */
export function buildTimePickPayload({ time, date, variant, surface }) {
    return { time, date, variant, surface };
}

export const __testing = Object.freeze({
    EXPERIMENT_KEY,
    ALLOWED_VARIANTS,
});
